import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSaleMandate, useDeleteSaleMandate } from '../../hooks/useSaleMandates';
import { Modal } from '../ui/Modal';
import { SaleMandateForm } from './SaleMandateForm';
import { MandateMatchTable } from './MandateMatchTable';
import { apiClient } from '../../api/client';
import { updateSaleMandateStatus } from '../../api/saleMandates';
import { useMandateImages, useUploadMandateImage, useUpdateMandateImage, useDeleteMandateImage } from '../../hooks/useMandateImages';
import { getMandateImageUrl, MandateImage } from '../../api/mandateImages';

const COUNTRY_NAMES: Record<string, string> = {
  DE: 'Deutschland', AT: 'Österreich', CH: 'Schweiz', ES: 'Spanien',
  FR: 'Frankreich', IT: 'Italien', NL: 'Niederlande', GB: 'Vereinigtes Königreich',
  PL: 'Polen', PT: 'Portugal', LU: 'Luxemburg', US: 'USA',
};

const STATUS_LABELS: Record<string, { label: string; bg: string; color: string }> = {
  active:     { label: 'Aktiv',         bg: 'var(--accent-light)',  color: 'var(--accent)' },
  versendet:  { label: 'Versendet',     bg: '#e3f2fd',              color: '#1565c0' },
  sold:       { label: 'Verkauft',      bg: '#e8f5e9',              color: '#2e7d32' },
  withdrawn:  { label: 'Zurückgezogen', bg: 'var(--surface2)',      color: 'var(--text3)' },
};

const RISK_LABELS: Record<string, string> = {
  core: 'Core', core_plus: 'Core+', value_add: 'Value Add', opportunistic: 'Opportunistisch',
};

function formatPrice(eur?: number | null): string {
  if (!eur) return '—';
  if (eur >= 1_000_000) return `${(eur / 1_000_000).toFixed(2).replace(/\.?0+$/, '')} Mio. €`;
  if (eur >= 1_000) return `${(eur / 1_000).toFixed(0)} Tsd. €`;
  return `${eur} €`;
}

type DetailTab = 'details' | 'bilder' | 'investoren';

const IMAGE_CATEGORIES = [
  { value: 'titelbild', label: 'Titelbild' },
  { value: 'aussenansicht', label: 'Außenansicht' },
  { value: 'innenansicht', label: 'Innenansicht' },
  { value: 'grundriss', label: 'Grundriss' },
  { value: 'lagekarte', label: 'Lagekarte' },
  { value: 'luftaufnahme', label: 'Luftaufnahme' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

function KV({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ color: 'var(--text3)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px' }}>{label}</div>
      <div style={{ fontSize: '13px', fontFamily: mono ? 'DM Mono, monospace' : undefined, fontWeight: mono ? 500 : undefined }}>{value}</div>
    </div>
  );
}

export function SaleMandateDetail({ id }: { id: string }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { data: mandate, isLoading, error, refetch } = useSaleMandate(id);
  const { mutate: deleteMandate, isPending: deleting } = useDeleteSaleMandate();

  const initialTab = (searchParams.get('tab') as DetailTab) || 'details';
  const [activeTab, setActiveTab] = useState<DetailTab>(initialTab);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [generatingExpose, setGeneratingExpose] = useState(false);
  const [generateMsg, setGenerateMsg] = useState<string | null>(null);

  const handleStatusChange = async (newStatus: string) => {
    setStatusUpdating(true);
    try {
      await updateSaleMandateStatus(id, newStatus);
      refetch();
    } finally {
      setStatusUpdating(false);
    }
  };


  const confirmDelete = () => {
    deleteMandate(id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['sale-mandates'] });
        navigate('/verkaufsmandate');
      },
    });
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '10px 20px', fontSize: '13px', fontWeight: active ? 600 : 400,
    color: active ? 'var(--accent)' : 'var(--text3)',
    background: 'none', border: 'none', cursor: 'pointer',
    borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
  });

  const fieldLabelStyle: React.CSSProperties = {
    color: 'var(--text3)', fontSize: '11px', textTransform: 'uppercase',
    letterSpacing: '0.3px', marginBottom: '4px',
  };

  if (isLoading) {
    return <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>;
  }

  if (error || !mandate) {
    return (
      <div style={{ padding: '60px', textAlign: 'center' }}>
        <div style={{ color: 'var(--accent)', marginBottom: '16px' }}>Mandat nicht gefunden.</div>
        <button onClick={() => navigate('/verkaufsmandate')} style={{ padding: '8px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: '13px' }}>
          Zurück zur Liste
        </button>
      </div>
    );
  }

  const statusInfo = STATUS_LABELS[mandate.status];
  const location = [mandate.street, mandate.city, mandate.country_code ? COUNTRY_NAMES[mandate.country_code] ?? mandate.country_code : null].filter(Boolean).join(', ');

  return (
    <div>
      {/* Back button */}
      <button
        onClick={() => navigate('/verkaufsmandate')}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '0',
          fontSize: '13px', color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: '4px',
          marginBottom: '16px',
        }}
        onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')}
        onMouseLeave={e => (e.currentTarget.style.color = 'var(--text3)')}
      >
        ← Zurück zu Verkaufsmandate
      </button>

      {/* Header Card */}
      <div className="card" style={{ padding: '24px 28px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, lineHeight: 1.3 }}>
              {mandate.project_name && (
                <span style={{ color: 'var(--text3)', fontWeight: 400, fontSize: '14px', marginRight: '8px' }}>{mandate.project_name} —</span>
              )}
              {mandate.title}
            </h2>
            {location && (
              <div style={{ fontSize: '14px', color: 'var(--text2)', marginTop: '6px' }}>
                {location}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
              <span style={{
                padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 500,
                background: statusInfo?.bg ?? 'var(--surface2)',
                color: statusInfo?.color ?? 'var(--text3)',
              }}>
                {statusInfo?.label ?? mandate.status}
              </span>
              {mandate.asset_classes?.map(ac => (
                <span key={ac} style={{
                  padding: '3px 10px', borderRadius: '20px', fontSize: '11px',
                  background: 'var(--accent-light)', color: 'var(--accent)', fontWeight: 500,
                }}>
                  {ac}
                </span>
              ))}
              {(mandate.send_count ?? 0) > 0 && (
                <span style={{ fontSize: '12px', color: 'var(--text3)' }}>
                  ✉ {mandate.send_count}× versendet
                </span>
              )}
            </div>
          </div>

          {/* Key metrics */}
          <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {mandate.asking_price_eur && (
              <div style={{ textAlign: 'right' }}>
                <div style={fieldLabelStyle}>Kaufpreis</div>
                <div style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, fontSize: '18px', color: 'var(--accent)' }}>
                  {formatPrice(mandate.asking_price_eur)}
                </div>
              </div>
            )}
            {mandate.size_sqm && (
              <div style={{ textAlign: 'right' }}>
                <div style={fieldLabelStyle}>Fläche</div>
                <div style={{ fontFamily: 'DM Mono, monospace', fontWeight: 500, fontSize: '15px' }}>
                  {mandate.size_sqm.toLocaleString('de-DE')} m²
                </div>
              </div>
            )}
            {mandate.noi_eur && (
              <div style={{ textAlign: 'right' }}>
                <div style={fieldLabelStyle}>NOI</div>
                <div style={{ fontFamily: 'DM Mono, monospace', fontWeight: 500, fontSize: '15px' }}>
                  {formatPrice(mandate.noi_eur)}
                </div>
              </div>
            )}
            {mandate.vermietungsstand_pct != null && mandate.vermietungsstand_pct > 0 && (
              <div style={{ textAlign: 'right' }}>
                <div style={fieldLabelStyle}>Vermietung</div>
                <div style={{ fontFamily: 'DM Mono, monospace', fontWeight: 500, fontSize: '15px' }}>
                  {mandate.vermietungsstand_pct} %
                </div>
              </div>
            )}
            {mandate.walt_years && (
              <div style={{ textAlign: 'right' }}>
                <div style={fieldLabelStyle}>WALT</div>
                <div style={{ fontFamily: 'DM Mono, monospace', fontWeight: 500, fontSize: '15px' }}>
                  {mandate.walt_years} J.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '18px', paddingTop: '16px', borderTop: '1px solid var(--border)', flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={() => setShowEditModal(true)}
            style={{ padding: '7px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text2)', cursor: 'pointer' }}>
            Bearbeiten
          </button>
          <button onClick={() => setShowDeleteModal(true)}
            style={{ padding: '7px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text3)', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text3)'; }}>
            Löschen
          </button>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text3)' }}>Status:</span>
            <select
              value={mandate.status}
              disabled={statusUpdating}
              onChange={e => handleStatusChange(e.target.value)}
              style={{
                padding: '5px 10px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
                fontSize: '13px', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer',
              }}>
              <option value="active">Aktiv</option>
              <option value="versendet">Versendet</option>
              <option value="sold">Verkauft</option>
              <option value="withdrawn">Zurückgezogen</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '20px', gap: 4 }}>
        <button style={tabStyle(activeTab === 'details')} onClick={() => setActiveTab('details')}>
          Details
        </button>
        <button style={tabStyle(activeTab === 'bilder')} onClick={() => setActiveTab('bilder')}>
          Bilder
        </button>
        <button
          onClick={() => setActiveTab('investoren')}
          style={{
            padding: '10px 20px', fontSize: '13px', fontWeight: 700,
            background: activeTab === 'investoren' ? 'var(--accent)' : 'var(--accent-light)',
            color: activeTab === 'investoren' ? '#fff' : 'var(--accent)',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            borderBottom: activeTab === 'investoren' ? '2px solid var(--accent)' : '2px solid transparent',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
            marginLeft: 4,
          }}
        >
          🎯 Passende Investoren
          {(mandate.match_count ?? 0) > 0 && (
            <span style={{
              background: activeTab === 'investoren' ? '#fff' : 'var(--accent)',
              color: activeTab === 'investoren' ? 'var(--accent)' : '#fff',
              padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 700,
            }}>{mandate.match_count}</span>
          )}
        </button>
      </div>

      {/* Tab content */}
      {activeTab === 'details' && (
        <div style={{ display: 'grid', gap: '20px' }}>
          {/* Kennzahlen */}
          <div className="card" style={{ padding: '24px 28px' }}>
            <div style={{ ...fieldLabelStyle, fontSize: '12px', fontWeight: 600, color: 'var(--accent)', marginBottom: '14px' }}>Kennzahlen</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px 28px', fontSize: '13px' }}>
              {mandate.risk_profile && <KV label="Risikoprofil" value={RISK_LABELS[mandate.risk_profile] ?? mandate.risk_profile} />}
              {mandate.transaction_structure && <KV label="Transaktionsstruktur" value={mandate.transaction_structure} />}
              {mandate.baujahr && <KV label="Baujahr" value={String(mandate.baujahr)} />}
              {mandate.modernisierung && <KV label="Modernisierung" value={mandate.modernisierung} />}
              {mandate.grundstueck_sqm && <KV label="Grundstück" value={`${Number(mandate.grundstueck_sqm).toLocaleString('de-DE')} m²`} />}
              {mandate.stellplaetze && <KV label="Stellplätze" value={String(mandate.stellplaetze)} />}
              {mandate.vermietungsstand_pct != null && mandate.vermietungsstand_pct > 0 && <KV label="Vermietungsquote" value={`${mandate.vermietungsstand_pct} %`} />}
              {mandate.leerstand_pct != null && mandate.leerstand_pct > 0 && <KV label="Leerstand" value={`${mandate.leerstand_pct} %`} />}
              {mandate.walt_years && <KV label="WALT" value={`${mandate.walt_years} Jahre`} />}
              {mandate.anzahl_mieter && <KV label="Anzahl Mieter" value={String(mandate.anzahl_mieter)} />}
              {mandate.ist_nettomiete_eur && <KV label="Ist-Nettomiete" value={formatPrice(mandate.ist_nettomiete_eur) + '/a'} mono />}
              {mandate.soll_nettomiete_eur && <KV label="Soll-Nettomiete" value={formatPrice(mandate.soll_nettomiete_eur) + '/a'} mono />}
              {mandate.noi_eur && <KV label="NOI" value={formatPrice(mandate.noi_eur) + '/a'} mono />}
              {mandate.mietindexierung && <KV label="Mietindexierung" value={mandate.mietindexierung} />}
              {mandate.capex_kurzfristig && <KV label="CapEx kurzfristig" value={mandate.capex_kurzfristig} />}
              {mandate.energieausweis && <KV label="Energieausweis" value={mandate.energieausweis} />}
              {mandate.prozess_phase && <KV label="Prozessphase" value={mandate.prozess_phase} />}
              {mandate.source && <KV label="Quelle" value={mandate.source === 'expose_import' ? 'Exposé-Import' : mandate.source === 'verkaufsinfo' ? 'Verkaufsinfo' : 'Manuell'} />}
              <KV label="Erstellt" value={new Date(mandate.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} />
            </div>
          </div>

          {/* Teaser-Texte */}
          {mandate.executive_summary && (
            <div className="card" style={{ padding: '24px 28px' }}>
              <div style={{ ...fieldLabelStyle, fontSize: '12px', fontWeight: 600, color: 'var(--accent)', marginBottom: '10px' }}>Executive Summary</div>
              <div style={{ color: 'var(--text)', lineHeight: 1.7, fontSize: '14px' }}>{mandate.executive_summary}</div>
            </div>
          )}

          {mandate.investment_highlights && mandate.investment_highlights.length > 0 && (
            <div className="card" style={{ padding: '24px 28px' }}>
              <div style={{ ...fieldLabelStyle, fontSize: '12px', fontWeight: 600, color: 'var(--accent)', marginBottom: '10px' }}>Investment Highlights</div>
              <ul style={{ margin: 0, paddingLeft: '20px', display: 'grid', gap: '6px' }}>
                {mandate.investment_highlights.map((h, i) => (
                  <li key={i} style={{ fontSize: '13px', lineHeight: 1.5, color: 'var(--text2)' }}>{h}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Text-Sektionen */}
          {[
            { label: 'Investmentstory', text: mandate.investment_story },
            { label: 'Objektbeschreibung', text: mandate.description },
            { label: 'Gebäudebeschreibung', text: mandate.gebaeude_beschreibung },
            { label: 'Standort — Makrolage', text: mandate.standort_makro },
            { label: 'Standort — Mikrolage', text: mandate.standort_mikro },
            { label: 'Standort — Marktrelevanz', text: mandate.standort_markt },
            { label: 'Mieterstruktur', text: mandate.mieterstruktur_text },
            { label: 'Ertragssituation', text: mandate.ertragssituation_text },
            { label: 'Wertsteigerungspotenzial', text: mandate.value_creation_text },
            { label: 'ESG / Nachhaltigkeit', text: mandate.esg_text },
            { label: 'Verkaufsprozess', text: mandate.verkaufsprozess_text },
          ].filter(s => s.text).map(s => (
            <div key={s.label} className="card" style={{ padding: '20px 28px' }}>
              <div style={{ ...fieldLabelStyle, fontSize: '12px', fontWeight: 600, color: 'var(--accent)', marginBottom: '8px' }}>{s.label}</div>
              <div style={{ color: 'var(--text2)', lineHeight: 1.6, fontSize: '13px', whiteSpace: 'pre-wrap' }}>{s.text}</div>
            </div>
          ))}

          {/* Team-Kontakte */}
          {mandate.team_contacts && mandate.team_contacts.length > 0 && (
            <div className="card" style={{ padding: '24px 28px' }}>
              <div style={{ ...fieldLabelStyle, fontSize: '12px', fontWeight: 600, color: 'var(--accent)', marginBottom: '12px' }}>Ansprechpartner</div>
              <div style={{ display: 'grid', gap: '10px' }}>
                {mandate.team_contacts.map(tc => (
                  <div key={tc.id} style={{ display: 'flex', gap: '16px', alignItems: 'center', padding: '10px 14px', background: 'var(--surface2)', borderRadius: 'var(--radius)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 500 }}>{tc.full_name}</div>
                      {tc.position && <div style={{ fontSize: '12px', color: 'var(--text3)' }}>{tc.position}</div>}
                    </div>
                    {tc.phone && <div style={{ fontSize: '12px', color: 'var(--text2)' }}>{tc.phone}</div>}
                    {tc.email && <div style={{ fontSize: '12px', color: 'var(--text2)', fontFamily: 'DM Mono, monospace' }}>{tc.email}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Exposé-Bereich */}
          <div className="card" style={{ padding: '24px 28px' }}>
            <div style={{ ...fieldLabelStyle, fontSize: '12px', fontWeight: 600, color: 'var(--accent)', marginBottom: '10px' }}>Exposé</div>
            <div style={{ marginTop: '8px' }}>
              {mandate.document_id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button type="button"
                    onClick={async () => {
                      try {
                        const resp = await apiClient.get(`/pdf/documents/${mandate.document_id}/file`, { responseType: 'blob' });
                        const url = URL.createObjectURL(resp.data as Blob);
                        window.open(url, '_blank');
                        setTimeout(() => URL.revokeObjectURL(url), 10000);
                      } catch { /* ignore */ }
                    }}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '13px', color: '#1565c0', display: 'flex', alignItems: 'center', gap: '6px' }}
                    onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                    onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}>
                    Exposé-PDF öffnen
                  </button>
                  <button type="button"
                    onClick={async () => {
                      try {
                        await apiClient.delete(`/pdf/documents/${mandate.document_id}`);
                        setGenerateMsg(null);
                        refetch();
                      } catch { /* ignore */ }
                    }}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '13px', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: '6px' }}
                    onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                    onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}>
                    Exposé löschen
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <button type="button" disabled={generatingExpose}
                    onClick={async () => {
                      setGeneratingExpose(true);
                      setGenerateMsg(null);
                      try {
                        const { data } = await apiClient.post(`/sale-mandates/${mandate.id}/generate-expose`);
                        setGenerateMsg(`Exposé erstellt (${data.data.filename})`);
                        refetch();
                      } catch (err: unknown) {
                        const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
                        setGenerateMsg(`Fehler: ${msg ?? 'Fehler beim Generieren'}`);
                      } finally {
                        setGeneratingExpose(false);
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      background: generatingExpose ? 'var(--surface2)' : '#e3f2fd',
                      border: '1px solid', borderColor: generatingExpose ? 'var(--border2)' : '#90caf9',
                      borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500,
                      cursor: generatingExpose ? 'not-allowed' : 'pointer',
                      color: generatingExpose ? 'var(--text3)' : '#1565c0',
                    }}>
                    {generatingExpose ? 'Wird generiert...' : 'Exposé generieren'}
                  </button>
                  <span style={{ fontSize: '12px', color: 'var(--text3)' }}>
                    Kein Exposé vorhanden — aus Mandatsdaten generieren
                  </span>
                </div>
              )}
              {generateMsg && (
                <div style={{
                  marginTop: '10px', fontSize: '13px', padding: '8px 12px', borderRadius: 'var(--radius)',
                  color: generateMsg.startsWith('Exposé erstellt') ? '#2e7d32' : 'var(--accent)',
                  background: generateMsg.startsWith('Exposé erstellt') ? '#e8f5e9' : 'var(--accent-light)',
                }}>
                  {generateMsg}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'bilder' && (
        <MandateImagesTab mandateId={mandate.id} />
      )}

      {activeTab === 'investoren' && (
        <div className="card" style={{ padding: '24px 28px' }}>
          <MandateMatchTable
            mandateId={mandate.id}
            mandateTitle={mandate.title}
            onSent={() => refetch()}
          />
        </div>
      )}

      {/* Modal: Bearbeiten */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Mandat bearbeiten" size="xl">
        <SaleMandateForm
          initial={mandate}
          onSuccess={() => { setShowEditModal(false); refetch(); }}
          onCancel={() => setShowEditModal(false)}
        />
      </Modal>

      {/* Modal: Löschen */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Mandat löschen" size="sm">
        <div style={{ fontSize: '14px', color: 'var(--text)', marginBottom: '20px' }}>
          <strong>{mandate.title}</strong> wirklich löschen?
          <br /><span style={{ color: 'var(--text3)', fontSize: '12px' }}>Diese Aktion kann nicht rückgängig gemacht werden.</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button onClick={() => setShowDeleteModal(false)}
            style={{ padding: '8px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', cursor: 'pointer' }}>
            Abbrechen
          </button>
          <button onClick={confirmDelete} disabled={deleting}
            style={{ padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
            {deleting ? 'Löschen...' : 'Löschen'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

// ── Bilder-Tab ──────────────────────────────────────────────────────
function MandateImagesTab({ mandateId }: { mandateId: string }) {
  const { data: images = [], isLoading } = useMandateImages(mandateId);
  const uploadMutation = useUploadMandateImage(mandateId);
  const updateMutation = useUpdateMandateImage(mandateId);
  const deleteMutation = useDeleteMandateImage(mandateId);
  const [uploadCategory, setUploadCategory] = useState('sonstiges');
  const [dragging, setDragging] = useState(false);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(file => {
      uploadMutation.mutate({ file, category: uploadCategory });
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const categoryLabel = (cat: string) =>
    IMAGE_CATEGORIES.find(c => c.value === cat)?.label ?? cat;

  return (
    <div style={{ display: 'grid', gap: '20px' }}>
      {/* Upload */}
      <div className="card" style={{ padding: '24px 28px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
          <select value={uploadCategory} onChange={e => setUploadCategory(e.target.value)}
            style={{ padding: '7px 12px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', background: 'var(--surface)', color: 'var(--text)' }}>
            {IMAGE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <label style={{ padding: '7px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
            Bild hochladen
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={e => handleFiles(e.target.files)} />
          </label>
          {uploadMutation.isPending && <span style={{ fontSize: '12px', color: 'var(--text3)' }}>Wird hochgeladen...</span>}
        </div>
        <div onDragOver={e => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={handleDrop}
          style={{ border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border2)'}`, borderRadius: 'var(--radius)', padding: '32px', textAlign: 'center', background: dragging ? 'var(--accent-light)' : 'var(--surface2)', color: 'var(--text3)', fontSize: '13px' }}>
          Bilder hierher ziehen oder oben auswählen (JPEG, PNG, WebP, max. 10 MB)
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>
      ) : images.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px' }}>Noch keine Bilder hochgeladen.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
          {images.map((img: MandateImage) => (
            <div key={img.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ position: 'relative', paddingTop: '66%', background: '#f0f0f0' }}>
                <img src={getMandateImageUrl(img.id)} alt={img.caption || img.filename}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ padding: '10px 12px' }}>
                <select value={img.category}
                  onChange={e => updateMutation.mutate({ imageId: img.id, updates: { category: e.target.value } })}
                  style={{ width: '100%', padding: '4px 8px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '11px', marginBottom: '6px', background: 'var(--surface)' }}>
                  {IMAGE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <input value={img.caption ?? ''} placeholder="Bildbeschreibung..."
                  onBlur={e => updateMutation.mutate({ imageId: img.id, updates: { caption: e.target.value } })}
                  onChange={() => {}}
                  onFocus={e => { const el = e.target; el.onchange = () => {}; }}
                  style={{ width: '100%', padding: '4px 8px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '11px', marginBottom: '6px', background: 'var(--surface)', boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '10px', color: 'var(--text3)' }}>
                    {categoryLabel(img.category)} · {(img.file_size_bytes / 1024).toFixed(0)} KB
                  </span>
                  <button type="button" onClick={() => deleteMutation.mutate(img.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: 'var(--accent)' }}>
                    Löschen
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
